import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useStory } from "@/hooks/use-baseline";

interface StoryRefDialogProps {
  storyId: string | null;
  onOpenChange: (open: boolean) => void;
}

export function StoryRefDialog({ storyId, onOpenChange }: StoryRefDialogProps) {
  const { data, isLoading } = useStory(storyId);

  return (
    <Dialog open={Boolean(storyId)} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[80vh] overflow-y-auto">
        {isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-7 w-3/4" />
            <Skeleton className="h-32 w-full mt-4" />
          </div>
        )}
        {data && (
          <>
            <DialogHeader>
              {(data.deliverableCode || data.epicCode) && (
                <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  {[data.deliverableCode, data.deliverableTitle].filter(Boolean).join(" — ")}
                  {data.epicCode && (
                    <>
                      <span className="mx-2">›</span>
                      {data.epicCode} {data.epicTitle}
                    </>
                  )}
                </div>
              )}
              <DialogTitle className="font-display text-lg leading-tight mt-1">
                <span className="font-mono text-sm text-muted-foreground mr-2">{data.code}</span>
                {data.label?.trim() || data.text}
              </DialogTitle>
              {data.label?.trim() && <p className="mt-2 text-sm text-foreground/80">{data.text}</p>}
            </DialogHeader>

            <section>
              <h3 className="font-display text-sm font-semibold mb-2">
                Acceptance criteria
                {data.acceptanceCriteria.length > 0 && (
                  <span className="ml-2 text-xs text-muted-foreground font-normal">
                    ({data.acceptanceCriteria.length})
                  </span>
                )}
              </h3>
              {data.acceptanceCriteria.length > 0 ? (
                <ol className="space-y-2">
                  {data.acceptanceCriteria.map((ac, i) => (
                    <li
                      key={ac.id}
                      className="flex gap-3 rounded-md border border-[var(--color-border)] p-3"
                    >
                      <span className="text-xs font-mono text-muted-foreground tabular-nums w-6 shrink-0">
                        {i + 1}.
                      </span>
                      <span className="text-sm text-foreground/85">{ac.text}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="rounded-md border border-dashed border-[var(--color-border)] p-4 text-sm text-muted-foreground">
                  No acceptance criteria.
                </div>
              )}
            </section>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
